<template>
  <v-row class="justify-center">
    <pie-chart
      v-if="!loadingChart"
      :styles="chartStyle"
      :chart-data="scoreData"
      :options="scoreOptions"
    />
    <log-dialog
      :headers="headers"
      :logData="logData"
      :showDialog="showDialog"
      :title="dialogTitle"
      :isLoading="loading"
      :csvFilename="csvFilename"
      :courseId="course.id"
    />
  </v-row>
</template>

<script>
import PieChart from './PieChart.vue'
import LogDialog from './LogDialog.vue'
import {
  passedFailedTitle,
  passedFailedTableHeader,
  getDuration,
  Multiset,
  roundToTwo,
  sortDataByDate,
  TOTAL_ITEMS,
  ellipsisLongText
} from '../Constants'
import store from '@/store/index'

export default {
  props: {
    course: {
      type: Object,
      required: true
    },
    chartStyle: {
      type: Object,
      default: null,
      required: false
    }
  },
  components: {
    PieChart,
    LogDialog
  },
  data() {
    return {
      scoreData: {},
      scoreOptions: {},
      loading: false,
      loadingChart: false,
      headers: [],
      logData: [],
      showDialog: false,
      dialogTitle: '',
      csvFilename: ''
    }
  },
  mounted() {
    this.loadingChart = true
    this.fillChartData()
    this.loadingChart = false
  },
  methods: {
    fillChartData() {
      const failed =
        this.course.scores !== undefined &&
        this.course.scores != null &&
        this.course.scores.failed !== undefined &&
        this.course.scores.failed != null
          ? this.course.scores.failed.length
          : 0

      const passed =
        this.course.scores !== undefined &&
        this.course.scores != null &&
        this.course.scores.passed !== undefined &&
        this.course.scores.passed != null
          ? this.course.scores.passed.length
          : 0

      this.scoreOptions = {
        hover: {
          onHover: function(e) {
            e.target.style.cursor = 'pointer'
          }
        },
        onClick: this.onClickScore,
        legend: {
          display: false
        },
        layout: {
          padding: {
            top: 15,
            right: 5,
            left: 5
          }
        },
        tooltips: {
          position: 'nearest',
          callbacks: {
            title: function(tooltipItem, data) {
              return ellipsisLongText(
                data['labels'][tooltipItem[0]['index']],
                31
              )
            },
            label: function(tooltipItem, data) {
              const scoreData =
                data['datasets'][0]['metadata'][tooltipItem['index']]
              let toolTip = new Array()
              if (scoreData !== undefined && scoreData != null) {
                scoreData.forEach(element => {
                  if (toolTip.length < TOTAL_ITEMS) {
                    toolTip.push(ellipsisLongText(element, 31))
                  }
                })

                if (scoreData.length > TOTAL_ITEMS) {
                  const diff = scoreData.length - TOTAL_ITEMS
                  toolTip.push(`and ${diff} more`)
                }
              }

              return toolTip
            }
          },
          backgroundColor: '#FFF',
          titleFontSize: 10,
          titleFontColor: '#000',
          bodyFontColor: '#000',
          bodyFontSize: 12,
          displayColors: false,
          borderWidth: 0.2,
          borderColor: '#000',
          hoverBorderColor: '#000',
          hoverBorderWidth: 10
        },
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 30,
        animation: {
          animateRotate: true,
          animateScale: true
        }
      }
      this.scoreData = {
        labels: ['Passed', 'Failed'],
        datasets: [
          {
            label: 'Score data',
            metadata: this.getScoreData(),
            backgroundColor: ['#7ed321', '#f5a623'],
            data: [passed, failed]
          }
        ]
      }
    },
    getScoreData() {
      let score = new Array()

      if (this.course.scores !== undefined && this.course.scores != null) {
        if (
          this.course.scores.passed !== undefined &&
          this.course.scores.passed != null
        ) {
          score.push(this.course.scores.passed)
        } else {
          score.push([])
        }

        if (
          this.course.scores.failed !== undefined &&
          this.course.scores.failed != null
        ) {
          score.push(this.course.scores.failed)
        } else {
          score.push([])
        }
      }

      return score
    },
    onClickScore(event, array) {
      this.logData = []
      this.showDialog = false
      if (array.length > 0) {
        let filter =
          array[0]._index === 1 ? ['failed'] : ['passed', 'completed']
        const courseName = this.course.name.replace(/\s+/g, '_')
        this.dialogTitle = passedFailedTitle
        this.headers = passedFailedTableHeader
        this.csvFilename = courseName + '_' + filter[0] + '_log'
        this.loading = true
        this.showDialog = true
        this.$store
          .dispatch('course/fetchCourse', this.course.id)
          .then(response => {
            if (
              response.interactions != null &&
              response.interactions !== undefined
            ) {
              this.logData = _.filter(response.interactions, function(o) {
                return filter.includes(o.verb)
              })

              this.logData.forEach(element => {
                element.duration = getDuration(element.duration)
                element.score = roundToTwo(element.score)
              })

              this.logData = sortDataByDate(this.logData)

              this.loading = false
            }
          })
          .catch(err => (this.loading = false))
      }
    }
  }
}
</script>

<style lang="scss">
$yellow: #f0bf19;
$orange: #ef602c;
$orange--dark: darken($orange, 10%);
$green: #0fc03e;
$green--dark: #0da836;

$color-bg: $orange--dark;
$color-bg: $orange--dark;
$color-primary: $green;
$color-primary--dark: $green--dark;
$color-accent: $yellow;

/* $Type
   ========================================================================== */
$base-font-size: 20px;
$base-scale: 1.4;
$base-rhythm: ceil($base-font-size * $base-scale);

/* $Fonts
   ========================================================================== */
$font-primary: 'Titillium Web', sans-serif;

.tooltip-wrap {
  display: inline-block;
  max-width: 100%;
  position: relative;
}

/**
	 * 1. Drop-shadow allows the shadow to cover 
	 * 	  everything including the triangle vs box-shadow
	 */

.tooltip {
  background-color: $color-primary;
  display: block;
  filter: drop-shadow(0 2px 8px rgba(black, 0.4)); /* 1 */
  font-size: 14px;
  padding: 1.4em;
  position: absolute;
  bottom: 145%;

  &:before {
    content: '';
    border: 15px solid transparent;
    border-top-color: $color-primary;
    position: absolute;
    left: 2.8em;
    top: 100%;
  }
}

.tooltip a {
  color: inherit;
}

.tooltip-in {
  animation: fade-in 0.2s ease-in-out forwards,
    tooltip-in 0.25s ease-in-out forwards;
}

.tooltip-out {
  animation: fade-in 0.2s ease-in-out forwards reverse,
    tooltip-out 0.25s ease-in-out forwards;
}

@keyframes tooltip-in {
  0% {
    transform: translateY(100%);
  }

  25% {
    transform: translateY(-15%);
  }

  35% {
    transform: translateY(5%);
  }

  45% {
    transform: translateY(-3%);
  }

  55% {
    transform: translateY(2%);
  }

  65% {
    transform: translateY(-2%);
  }

  100% {
    transform: translateY(0);
  }
}

@keyframes tooltip-out {
  0% {
    transform: translateY(0);
  }

  25% {
    transform: translateY(5%);
  }

  100% {
    transform: translateY(100%);
  }
}
</style>
